export const initComponents = async () => {
  // Set up message listener for embedded tokens at the root level
  // This needs to be done early to catch tokens before any components mount
  const handleMessage = (event: MessageEvent) => {
    // In production, verify the origin for security
    // if (event.origin !== 'https://expected-parent-domain.com') return;

    if (event.data?.type === 'GEKKO_AUTH_TOKENS') {
      const { payload } = event.data;
      console.log('initComponents: Received auth tokens from parent:', payload);

      // Store embedded tokens for API calls
      window.sessionStorage.setItem(
        'gekko/embeddedTokens',
        JSON.stringify({
          accessToken: payload.accessToken,
          idToken: payload.idToken,
          userSub: payload.userSub,
          username: payload.username,
        })
      );

      console.log('initComponents: Stored embedded tokens in sessionStorage');
    }
  };

  // Add the message listener immediately
  window.addEventListener('message', handleMessage);

  // Store the cleanup function globally so it can be called if needed
  (window as any).__cleanupMessageListener = () => {
    window.removeEventListener('message', handleMessage);
  };

  // Check if we're coming back from Cognito redirect
  const url = new URL(window.location.href);
  const urlParams = new URLSearchParams(url.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');

  console.log('initComponents: Current URL:', window.location.href);
  console.log('initComponents: Code:', code);
  console.log('initComponents: State:', state);

  if (code && state) {
    console.log(
      'initComponents: Found code and state, storing return URL but keeping params for OAuth'
    );
    // We're coming back from Cognito login
    // The state parameter contains the original components URL we should return to
    // Store it in sessionStorage and let the normal OAuth flow complete first
    window.sessionStorage.setItem('gekko/componentsReturnUrl', state);
    console.log('initComponents: Stored return URL:', state);

    // DON'T remove the code and state yet - let the main app handle OAuth first
    // The useAuthentication hook will handle the redirect after authentication

    // Return false to prevent components initialization, let the main app handle OAuth
    console.log('initComponents: Returning false to let main app handle OAuth');
    return false;
  }

  // Check if we just completed OAuth and should return to a components URL
  const componentsReturnUrl = window.sessionStorage.getItem(
    'gekko/componentsReturnUrl'
  );
  console.log(
    'initComponents: Checking for stored return URL:',
    componentsReturnUrl
  );
  if (componentsReturnUrl) {
    window.sessionStorage.removeItem('gekko/componentsReturnUrl');
    console.log(
      'initComponents: Redirecting to components URL:',
      componentsReturnUrl
    );
    // Redirect to the original components URL
    window.location.href = componentsReturnUrl;
    return false;
  }

  // URL is in format /components/:component/:sessionId?signature=<signature>&timestamp=<timestamp>
  // extract information from current URL and store
  const re = /\/components\/([^/]+)\/([^/]+)(.*)/;
  const matches = url.pathname.match(re);
  if (!matches) {
    console.error('Invalid URL');
    return false;
  }
  const component = matches[1];
  const sessionId = matches[2];
  const searchParams = new URLSearchParams(url.search);

  (window as any).__componentsInitialData = {
    component,
    sessionId,
    searchParams,
  };
  //window.history.replaceState({}, '', '/');
  const componentsCallback: ComponentsCallback = (
    type: string,
    payload: any
  ) => {
    try {
      if (window.parent !== window) {
        window.parent.postMessage(
          JSON.stringify({
            timestamp: Date.now(),
            type,
            payload,
          }),
          '*'
        );
      } else {
        console.log('componentsCallback', type, payload);
      }
    } catch (e) {
      console.error('Failed to post message to parent', e);
    }
  };
  (window as any).__componentsCallback = componentsCallback;

  componentsCallback('init', null);

  const pmsIntegration: PMSIntegration = {
    handleSave: async () => {
      return;
    },
    waitAndClose: (work?: () => Promise<void>) => {
      return async () => {
        if (work) await work();
        Promise.all(pmsIntegration.pending).then(() => {
          componentsCallback('close', null);
        });
      };
    },
    pending: [],
    saveSummary: async (text) => {
      componentsCallback('saveSummary', text);
    },
  };

  (window as any).pmsIntegration = pmsIntegration;

  return true; // Indicate that initialization completed successfully
};

export type PMSIntegration = {
  handleSave: (force?: boolean) => Promise<void>;
  waitAndClose: (work?: () => Promise<void>) => () => void;
  pending: Promise<void>[]; // list of pending promises
  saveSummary: (text: string) => Promise<void>;
};

export type ComponentsCallback = (type: string, payload: any) => void;
